import { useMutation } from '@tanstack/react-query';
import { css } from 'aphrodite';
import * as React from 'react';
import { useState } from 'react';
import { toast } from 'sonner';
import { CoffeeLeadIssueCategory, ICreateCoffeeLeadIssueRequest } from '../../../extViewmodels';
import { Noop } from '../../../extViewmodels/Utils';
import { useEventLogging } from '../../../models/Logging';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { useModal } from '../../../models/hooks/useModal';
import { Button } from '../../../web/components/Button';
import { RadioButton } from '../../../web/components/RadioButton';
import { XIcon } from '../../../web/components/svgs/icons/XIcon';
import { aidaBaseStyleSheet } from '../../styles/styles';
import { BlueModal } from '../BlueModal';
import { TransparentButton } from '../TransparentButton';
import { styleSheet } from './styles';

const CategoryOptions: { label: string; value: CoffeeLeadIssueCategory }[] = [
	{ label: 'Wrong Location/Timezone', value: CoffeeLeadIssueCategory.WrongLocationOrTimeZone },
	{ label: 'Duplicate', value: CoffeeLeadIssueCategory.Duplicate },
	{ label: 'Current Client Needs Attention', value: CoffeeLeadIssueCategory.CurrentClientNeedsAttention },
	{ label: 'Other', value: CoffeeLeadIssueCategory.Other },
];

interface IProps {
	companyId: string;
	companyName: string;
}

export const ReportIssueModal: React.FC<IProps> = ({ companyId, companyName }) => {
	const userSession = useUserSession();
	const { logApiError } = useEventLogging('ReportIssueModal');
	const [category, setCategory] = useState<CoffeeLeadIssueCategory | null>(null);
	const [description, setDescription] = useState('');

	const resetForm = () => {
		setCategory(null);
		setDescription('');
	};

	const modal = useModal(false, Noop, [], false, false);

	const reportIssueMutation = useMutation({
		mutationFn: async (request: ICreateCoffeeLeadIssueRequest) => {
			const result = await userSession.webServiceHelper.callWebServiceAsync('coffeeleadissue', 'POST', request);
			if (!result.success) {
				throw result;
			}
			return result;
		},
		onSuccess: () => {
			toast.success('Issue reported successfully.');
			modal.onRequestClose(null, true);
			resetForm();
		},
		onError: (error: unknown) => {
			logApiError('ReportIssue-Error', error);
			toast.error('Failed to report issue. Please try again.');
		},
	});

	const onOpenClick = () => {
		resetForm();
		modal.setIsOpen(true)();
	};

	const onCloseClick = () => {
		modal.onRequestClose(null, true);
		resetForm();
	};

	const onSubmit = () => {
		if (!category || !description.trim()) {
			return;
		}

		reportIssueMutation.mutate({
			category,
			companyId,
			description: description.trim(),
		});
	};

	const disabled = !category || !description.trim() || reportIssueMutation.isPending;

	const renderHeader = () => {
		return (
			<div className={css(styleSheet.header)}>
				<div className={css(styleSheet.title)}>Report an issue with {companyName}</div>
				<TransparentButton className={css(styleSheet.close)} onClick={onCloseClick}>
					<XIcon height={12} width={12} />
				</TransparentButton>
			</div>
		);
	};

	return (
		<div className={css(styleSheet.container)}>
			<TransparentButton className={css(styleSheet.triggerButton)} onClick={onOpenClick}>
				Report Issue
			</TransparentButton>
			<BlueModal className={css(styleSheet.modal)} headerAccessory={renderHeader()} {...modal}>
				<div className={css(styleSheet.mainContainer)}>
					<div className={css(styleSheet.sectionLabel)}>Category</div>
					<div className={css(styleSheet.radioOptionsContainer)}>
						{CategoryOptions.map(option => (
							<div className={css(styleSheet.radioOptionContainer)} key={option.value}>
								<RadioButton
									checked={category === option.value}
									id={`issue-category-radio-${option.value}`}
									onChange={() => setCategory(option.value)}
								>
									<span className={css(styleSheet.radioLabel)}>{option.label}</span>
								</RadioButton>
							</div>
						))}
					</div>
					<div className={css(styleSheet.sectionLabel)}>Description</div>
					<textarea
						className={css(styleSheet.textarea)}
						maxLength={1000}
						placeholder='Describe the issue...'
						value={description}
						onChange={e => setDescription(e.target.value)}
					/>
					<div className={css(styleSheet.charCount)}>{description.length}/1000</div>
				</div>
				<div className={css(styleSheet.buttonContainer)}>
					<Button
						label={reportIssueMutation.isPending ? 'Submitting...' : 'Submit'}
						onClick={onSubmit}
						disabled={disabled}
						className={css(disabled && aidaBaseStyleSheet.disabled)}
					/>
					<Button label='Cancel' kind='reverse' onClick={onCloseClick} />
				</div>
			</BlueModal>
		</div>
	);
};
